import { ApiError } from "../api/client.js";
import type { ProxyLookup, RefusedStep } from "../api/types.js";
import { proxyTail } from "../lib/format.js";

/**
 * DCTA-120 — 17 Payees at `add` (review P1-2, spec §F.2 5c; mock-up 17-payees.html `add`, `add-found`,
 * `add-blocked`). The bank's add-payee screen: the customer types a PayNow mobile, NRIC/FIN, UEN or
 * wallet address; the bank's directory answers with the name registered with PayNow (masked, as PayNow
 * shows it) — before anything can be saved. DCTA-124 (§F.2 row 13): saving is the bank's SIGNED
 * `payee_add` step — the customer's own name for them, Check and sign (app/add-flow.ts), the passkey;
 * the interim "call us to add one" end retired with it (13l).
 *
 * Nothing here parses what the bank said: a proxy is read from the CUSTOMER's typing only, to choose
 * the directory's `proxy_type`; the name and the masked proxy are the directory's, as text.
 */
export type ProxyType = "mobile" | "nric_fin" | "uen" | "vpa";

export interface ProxyInput {
  readonly proxy_type: ProxyType;
  readonly proxy: string;
}

/** Copy for the screen. §E.6 / 17's own words where the deck has them; the rest flagged for ux-designer (DCTA-120). */
export const ADD_COPY = {
  title: "Add a payee",
  label: "PayNow mobile, NRIC or UEN",
  help: "We'll check the name registered with PayNow.",
  continue: "Continue",
  found: "Name registered with PayNow",
  talk: "Talk to us",
  /** New (flagged): the field holds nothing the directory can read. */
  invalid: "Enter a PayNow mobile number, NRIC or UEN.",
  /** New (flagged), after the accepted "Couldn't reach the bank. Nothing left your accounts." */
  unreachable: "Couldn't reach the bank. Nothing was added.",
  /** §E.6 / 17 `add-found`: the customer's own name for them (optional). */
  nickname: { label: "Your name for them", help: "Only you see this name." },
  /** §F.2 13a: the action once the name is shown. */
  check: "Check and sign",
  /** §F.2 13j (§E.6, verbatim): the bank would not add this number and sent no sentence of its own. */
  blocked: "We can't set up payments to this number here. If you need to, please call us.",
  /** New (DCTA-124, flagged for ux-designer): a name the bank would not keep (its display rule: 40 characters, no < > or hidden characters). */
  nicknameLong: "Use 40 characters or fewer.",
  nicknameOdd: "Use letters, numbers and spaces only.",
  /** New (DCTA-124, flagged): the bank could not be reached while it built the card — 16's error state in an add's words. */
  noCard: { lead: "Couldn't reach the bank", rest: "Nothing was added." },
  /** New (DCTA-124, flagged): §C.11's first-signature line, in an add's words (it is not a payment). */
  firstSign: "First signature here: your phone saves a passkey, then signs with it.",
  /** §C.13. */
  saved: "Passkey saved.",
} as const;

/** §F.2 13f: the toast once the bank has added them — the name the row prints (the customer's, else the registered one). */
export function addedLine(name: string): string {
  return `${name} added`;
}

/** What 17 asks the bank for (`POST /v1/payees/additions`): the number, and the customer's own name for them when given. */
export interface PayeeAddRequest {
  readonly proxy_type: ProxyType;
  readonly proxy: string;
  readonly nickname?: string;
}

export function addRequest(proxy: ProxyInput, nickname: string): PayeeAddRequest {
  const n = nickname.trim();
  return { proxy_type: proxy.proxy_type, proxy: proxy.proxy, ...(n === "" ? {} : { nickname: n }) };
}

/** The bank's display rule for a nickname (lab/dcta bank/views.ts NICKNAME_PATTERN), checked before asking: no markup, controls or invisible characters. */
/** The bank's display rule as a test for what it refuses (lab/dcta bank/views.ts NICKNAME_PATTERN) — 17's nickname, DCTA-176's occupation. */
export const DISPLAY_ODD = /[<>\u0000-\u001f\u007f-\u009f\u00ad\u200b-\u200f\u2028\u2029\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff]/;

/** A nickname the bank would not keep, said about the field — or nothing (an empty one is no nickname). UX only: the bank is the check. */
export function nicknameProblem(typed: string): string | undefined {
  const n = typed.trim();
  if (n === "") return undefined;
  if (n.length > 40) return ADD_COPY.nicknameLong;
  if (DISPLAY_ODD.test(n)) return ADD_COPY.nicknameOdd;
  return undefined;
}

/**
 * The bank would not build the card (`POST /v1/payees/additions` refused: KYC, screening, too many new
 * payees today, already saved, not registered): its refusal as a refused step, for the bank's refusal
 * card — its sentence verbatim (§E.7: "never a form error that pretends to be the bank"), its next
 * steps and actions, and at most one rule id (none for a screening hit: the bank sends none).
 */
export function addRefusal(error: ApiError): RefusedStep {
  const b = error.body;
  const rules = Array.isArray(b["rule_ids"]) ? (b["rule_ids"] as unknown[]).filter((r): r is string => typeof r === "string") : [];
  const steps = Array.isArray(b["next_steps"]) ? (b["next_steps"] as unknown[]).filter((r): r is string => typeof r === "string") : undefined;
  const actions = Array.isArray(b["next_actions"]) ? (b["next_actions"] as RefusedStep["next_actions"]) : undefined;
  return {
    step_id: "s1",
    source_index: "1",
    reason_code: error.reasonCode,
    rule_ids: rules,
    customer_message: error.serverMessage ?? (error.reasonCode === "POLICY_SCREENING_BLOCKED" ? ADD_COPY.blocked : "Nothing was added."),
    ...(steps === undefined || steps.length === 0 ? {} : { next_steps: steps }),
    ...(actions === undefined || actions.length === 0 ? {} : { next_actions: actions }),
  };
}


const LABEL: Readonly<Record<string, string>> = {
  mobile: "PayNow mobile",
  nric_fin: "PayNow NRIC/FIN",
  uen: "PayNow UEN",
  vpa: "PayNow address",
};

/** The PayNow label for a proxy type, as the design prints it (§F.2 6g: a VPA is a "PayNow address", never "VPA"). */
export function proxyLabel(type: string): string {
  return LABEL[type] ?? "PayNow";
}

/** What the customer typed, as one PayNow proxy the directory can look up — or nothing (nothing is sent). */
export function proxyOf(typed: string): ProxyInput | undefined {
  const t = typed.trim();
  if (t.includes("@")) {
    const v = t.replace(/\s+/g, "").toLowerCase();
    return /^[a-z0-9._-]+@[a-z0-9.-]+$/.test(v) ? { proxy_type: "vpa", proxy: v } : undefined;
  }
  const c = t.replace(/[\s-]/g, "").toUpperCase();
  const mobile = /^(?:\+?65)?([89]\d{7})$/.exec(c);
  if (mobile !== null) return { proxy_type: "mobile", proxy: `+65${mobile[1] as string}` };
  if (/^[STFGM]\d{7}[A-Z]$/.test(c)) return { proxy_type: "nric_fin", proxy: c };
  if (/^\d{8,9}[A-Z]$/.test(c) || /^[TSR]\d{2}[A-Z]{2}\d{4}[A-Z]$/.test(c)) return { proxy_type: "uen", proxy: c };
  return undefined;
}

/** The screen's state: typing · checking · the directory's name · a sentence about the field. */
export type AddState =
  | { readonly kind: "ask" }
  | { readonly kind: "checking" }
  | { readonly kind: "found"; readonly initial: string; readonly name: string; readonly sub: string }
  | { readonly kind: "error"; readonly text: string; readonly talk: boolean };

export function foundState(r: ProxyLookup): AddState {
  const name = r.registered_name;
  return { kind: "found", initial: (/\p{L}|\p{N}/u.exec(name)?.[0] ?? "·").toUpperCase(), name, sub: `${proxyLabel(r.proxy_type)} ${proxyTail(r.masked_proxy)}` };
}

/**
 * A lookup that did not find a name: the bank's own sentence where it sent one (not registered; a
 * screening hit, neutral by design — no rule id, no reason, and Talk to us), else what the screen knows.
 */
export function lookupFailed(error: unknown): AddState {
  if (error instanceof ApiError) {
    if (error.reasonCode === "PAYEE_PROXY_UNREGISTERED") return { kind: "error", text: error.serverMessage ?? "That PayNow number isn't registered. Nothing was added.", talk: false };
    // DCTA-126 (§F.2 13i): the name check is down — a 503, and still the bank's own sentence about this number.
    if (error.reasonCode === "SCREENING_UNAVAILABLE" && error.serverMessage !== undefined) return { kind: "error", text: error.serverMessage, talk: false };
    // §F.2 13j: the bank's neutral sentence; §E.6's own words when it sent none (the old fallback retires).
    if (error.reasonCode === "POLICY_SCREENING_BLOCKED") return { kind: "error", text: error.serverMessage ?? ADD_COPY.blocked, talk: true };
    if (error.status === 400) return { kind: "error", text: ADD_COPY.invalid, talk: false };
  }
  return { kind: "error", text: ADD_COPY.unreachable, talk: false };
}
